import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { GLAccount } from './glAccountServices';
import { GLTransaction } from './glTransactionServices';

// Define interfaces for Profit & Loss statement
export interface ProfitLossItem {
  accountId: string;
  accountNumber: string;
  accountName: string;
  amount: number;
  category: 'revenue' | 'expense' | 'cogs' | 'writeoff';
}

export interface ProfitLossCategory {
  name: string;
  items: ProfitLossItem[];
  total: number;
}

export interface ProfitLossData {
  revenue: ProfitLossCategory;
  cogs: ProfitLossCategory;
  grossProfit: number;
  expenses: ProfitLossCategory;
  netProfit: number;
  startDate?: Date;
  endDate?: Date;
}

/**
 * Generates a Profit & Loss statement for a given period
 * @param startDate Optional start date for filtering transactions
 * @param endDate Optional end date for filtering transactions
 * @returns Promise with ProfitLossData
 */
export const generateProfitLoss = async (
  startDate?: Date,
  endDate?: Date
): Promise<ProfitLossData> => {
  try {
    // Step 1: Get all GL accounts
    const accountsSnapshot = await getDocs(collection(db, 'glAccounts'));
    const accounts: GLAccount[] = [];
    accountsSnapshot.forEach((doc) => {
      accounts.push({ id: doc.id, ...doc.data() } as GLAccount);
    });

    // Step 2: Get all transactions within date range if specified
    let transactionsQuery;
    
    if (startDate || endDate) {
      const filters = [];
      
      if (startDate) {
        filters.push(where('transactionDate', '>=', Timestamp.fromDate(startDate)));
      }
      
      if (endDate) {
        // Add one day to include transactions on the end date
        const nextDay = new Date(endDate);
        nextDay.setDate(nextDay.getDate() + 1);
        filters.push(where('transactionDate', '<', Timestamp.fromDate(nextDay)));
      }
      
      transactionsQuery = query(collection(db, 'glTransactions'), ...filters);
    } else {
      transactionsQuery = collection(db, 'glTransactions');
    }
    
    const transactionsSnapshot = await getDocs(transactionsQuery);
    const transactions: GLTransaction[] = [];
    
    transactionsSnapshot.forEach((doc) => {
      const data = doc.data();
      const transaction = {
        id: doc.id,
        ...data,
        transactionDate: data.transactionDate instanceof Timestamp 
          ? data.transactionDate.toDate() 
          : data.transactionDate,
        entries: data.entries || []
      } as GLTransaction;
      
      transactions.push(transaction);
    });

    // Step 3: Calculate balances for each account
    const accountBalances: Record<string, number> = {};
    
    // Initialize all accounts with zero balances
    accounts.forEach(account => {
      if (account.id) {
        accountBalances[account.id] = 0;
      }
    });
    
    // Process all transaction entries
    transactions.forEach(transaction => {
      transaction.entries.forEach((entry) => {
        if (!entry.accountId) return;
        
        if (accountBalances[entry.accountId] === undefined) {
          accountBalances[entry.accountId] = 0;
        }
        
        // For P&L, we need to calculate the net effect
        // Credit increases revenue accounts, debit increases expense accounts
        accountBalances[entry.accountId] += entry.credit - entry.debit;
      });
    });

    // Step 4: Create P&L items
    const revenueItems: ProfitLossItem[] = [];
    const cogsItems: ProfitLossItem[] = [];
    const expenseItems: ProfitLossItem[] = [];
    
    accounts.forEach(account => {
      if (!account.id) return;
      
      const balance = accountBalances[account.id];
      
      // Skip accounts with zero balance
      if (balance === 0) return;
      
      // Determine account category based on account number
      const accountNumber = account.accountNumber;
      const firstDigit = accountNumber.charAt(0);
      
      if (firstDigit === '6') { // Revenue
        revenueItems.push({
          accountId: account.id,
          accountNumber: account.accountNumber,
          accountName: account.accountName,
          amount: Math.abs(balance), // Revenue is normally a credit balance
          category: 'revenue'
        });
      } else if (firstDigit === '8') { // COGS (Cost of Goods Sold)
        cogsItems.push({
          accountId: account.id,
          accountNumber: account.accountNumber,
          accountName: account.accountName,
          amount: Math.abs(balance), // COGS is normally a debit balance
          category: 'cogs'
        });
      } else if (firstDigit === '7') { // All Expenses (including inventory write-offs)
        expenseItems.push({
          accountId: account.id,
          accountNumber: account.accountNumber,
          accountName: account.accountName,
          amount: Math.abs(balance), // Expense is normally a debit balance
          category: 'expense'
        });
      }
    });
    
    // Sort items by account number
    revenueItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    cogsItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    expenseItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    
    // Calculate totals
    const totalRevenue = revenueItems.reduce((sum: number, item: ProfitLossItem) => sum + item.amount, 0);
    const totalCOGS = cogsItems.reduce((sum: number, item: ProfitLossItem) => sum + item.amount, 0);
    const totalExpenses = expenseItems.reduce((sum: number, item: ProfitLossItem) => sum + item.amount, 0);
    
    // Calculate gross profit (Revenue - COGS)
    const grossProfit = totalRevenue - totalCOGS;
    
    // Calculate net profit (Gross Profit - Expenses)
    const netProfit = grossProfit - totalExpenses;
    
    return {
      revenue: {
        name: 'Revenue',
        items: revenueItems,
        total: totalRevenue
      },
      cogs: {
        name: 'Cost of Goods Sold',
        items: cogsItems,
        total: totalCOGS
      },
      grossProfit,
      expenses: {
        name: 'Expenses',
        items: expenseItems,
        total: totalExpenses
      },
      netProfit,
      startDate,
      endDate
    };
  } catch (error) {
    console.error('Error generating profit & loss statement:', error);
    throw error;
  }
};
